import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../store/auth.js';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';
import AutoPRPanel from '../components/AutoPRPanel.jsx';

const STATUSES = ['new', 'triaged', 'in_progress', 'shipped', 'declined', 'duplicate', 'acknowledged', 'resolved'];
const CATEGORIES = ['bug', 'feature', 'function', 'error_5xx', 'error_4xx', 'error_validation', 'error_unhandled', 'error_frontend'];
const SOURCES = ['user', 'auto_error', 'auto_error_unhandled', 'auto_error_frontend'];

export default function AdminFeedback() {
  const { agent } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', severity: '', source: '' });
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');

  const isSuper = agent?.role === 'superadmin';

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.category) params.category = filters.category;
      if (filters.severity) params.severity = filters.severity;
      if (filters.source) params.source = filters.source;
      const r = await apiGet('/feedback/admin/list', params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isSuper) return;
    const idFromUrl = new URLSearchParams(window.location.search).get('id');
    load();
    if (idFromUrl) {
      apiGet(`/feedback/admin/${idFromUrl}`).then((r) => r?.feedback && setSelected(r.feedback)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.category, filters.severity, filters.source]);

  async function changeStatus(id, status) {
    try {
      const r = await apiPatch(`/feedback/admin/${id}`, { status });
      toast.success(`Status → ${status}`);
      setSelected(r.feedback);
      load();
    } catch (err) { toast.error(err.message || 'Failed'); }
  }

  async function addComment() {
    if (!selected || !comment.trim()) return;
    try {
      const r = await apiPatch(`/feedback/admin/${selected._id}`, { comment });
      setSelected(r.feedback);
      setComment('');
    } catch (err) { toast.error(err.message || 'Failed'); }
  }

  async function retriage() {
    if (!selected) return;
    try {
      await apiPost(`/feedback/admin/${selected._id}/retriage`, {});
      toast.success('Retriage queued');
      const r = await apiGet(`/feedback/admin/${selected._id}`);
      setSelected(r.feedback);
    } catch (err) { toast.error(err.message || 'Failed'); }
  }

  if (!isSuper) {
    return <div className="p-6 text-gray-300">Superadmin only.</div>;
  }

  const headerCounts = useMemo(() => {
    const c = items.reduce((acc, it) => {
      acc[it.category] = (acc[it.category] || 0) + 1;
      return acc;
    }, {});
    return c;
  }, [items]);

  return (
    <div className="p-6 text-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#E040FB' }}>Feedback Inbox</h1>
          <div className="text-sm text-gray-400">All tenants · {total} total · {items.filter((i) => i.status === 'new').length} new in view</div>
        </div>
        <div className="flex gap-2 text-xs">
          {Object.entries(headerCounts).map(([k, v]) => (
            <span key={k} className="px-2 py-1 rounded-sm" style={{ background: '#13132a', border: '1px solid #252550' }}>{k}: {v}</span>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4 text-sm">
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-2 py-1 rounded-sm" style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}>
          <option value="">all statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          className="px-2 py-1 rounded-sm" style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}>
          <option value="">all categories</option>
          {CATEGORIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}
          className="px-2 py-1 rounded-sm" style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}>
          <option value="">all sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="px-3 py-1 rounded-sm text-xs" style={{ background: '#3B82F6', color: '#fff' }}>Refresh</button>
      </div>

      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: '#252550' }}>
        <table className="w-full text-sm">
          <thead style={{ background: '#13132a' }}>
            <tr className="text-left text-gray-400">
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Tenant</th>
              <th className="px-3 py-2">Reporter</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Sev</th>
              <th className="px-3 py-2">Title / Route</th>
              <th className="px-3 py-2"># </th>
              <th className="px-3 py-2">AI Triage</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="px-3 py-4 text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={10} className="px-3 py-4 text-gray-500">No feedback yet.</td></tr>}
            {items.map((it) => (
              <tr key={it._id} onClick={() => setSelected(it)} className="cursor-pointer hover:bg-[#13132a]" style={{ borderTop: '1px solid #252550' }}>
                <td className="px-3 py-2 text-gray-400 text-xs">{new Date(it.createdAt).toISOString().slice(0, 16).replace('T', ' ')}</td>
                <td className="px-3 py-2 text-xs">{it.tenantId ? `…${String(it.tenantId).slice(-6)}` : '—'}</td>
                <td className="px-3 py-2 text-xs">{it.agentEmail || (it.source && it.source.startsWith('auto_') ? '(auto)' : '—')}</td>
                <td className="px-3 py-2 text-xs">{it.source || 'user'}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-sm text-xs" style={{ background: '#7C3AED', color: '#fff' }}>{it.category}</span></td>
                <td className="px-3 py-2 text-xs">{it.severity || '—'}</td>
                <td className="px-3 py-2">{it.title}{it.route ? <div className="text-[11px] text-gray-500 font-mono">{it.route}</div> : null}</td>
                <td className="px-3 py-2 text-xs">{it.count > 1 ? <span className="px-1.5 py-0.5 rounded-sm" style={{ background: '#3B82F6', color: '#fff' }}>×{it.count}</span> : (it.count || 1)}</td>
                <td className="px-3 py-2 text-xs text-gray-400">{it.triage?.severityRecommendation ? `${it.triage.severityRecommendation} / ${it.triage.effortEstimate || '—'}` : '—'}</td>
                <td className="px-3 py-2 text-xs">{it.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-y-0 right-0 w-[520px] z-40 overflow-y-auto p-5" style={{ background: '#0b0b1a', borderLeft: '1px solid #252550' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-gray-500">{selected.category} · {new Date(selected.createdAt).toISOString().slice(0, 16).replace('T', ' ')}</div>
              <h3 className="text-lg font-semibold mt-1">{selected.title}</h3>
              <div className="text-xs text-gray-400">{selected.agentEmail} · tenant …{String(selected.tenantId).slice(-6)}</div>
            </div>
            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-white">×</button>
          </div>
          <div className="mt-4 text-sm whitespace-pre-wrap">{selected.description}</div>
          {selected.screenshotUrl && <div className="mt-3"><img src={selected.screenshotUrl} alt="screenshot" className="max-w-full rounded-sm border" style={{ borderColor: '#252550' }} /></div>}
          <div className="mt-4 text-xs text-gray-400">
            <div>page: {selected.context?.pageUrl || '—'}</div>
            <div>ua: {selected.context?.userAgent || '—'}</div>
            <div>viewport: {selected.context?.viewport || '—'}</div>
          </div>
          {(selected.context?.consoleErrors || []).length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-gray-400 cursor-pointer">Console errors ({selected.context.consoleErrors.length})</summary>
              <pre className="text-[11px] text-gray-400 whitespace-pre-wrap mt-2">{selected.context.consoleErrors.join('\n')}</pre>
            </details>
          )}
          {selected.source && selected.source.startsWith('auto_') && (
            <div className="mt-4 p-3 rounded-sm text-xs" style={{ background: '#13132a', borderLeft: '3px solid #E040FB' }}>
              <div className="uppercase mb-2" style={{ color: '#E040FB' }}>Auto-Captured Error</div>
              <div className="grid grid-cols-2 gap-y-1 text-gray-300">
                <div className="text-gray-500">count</div><div>×{selected.count || 1}</div>
                <div className="text-gray-500">first seen</div><div>{selected.firstSeenAt ? new Date(selected.firstSeenAt).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—'}</div>
                <div className="text-gray-500">last seen</div><div>{selected.lastSeenAt ? new Date(selected.lastSeenAt).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—'}</div>
                <div className="text-gray-500">route</div><div className="font-mono">{selected.route || '—'}</div>
                <div className="text-gray-500">errorId</div><div className="font-mono">{selected.errorId || '—'}</div>
                <div className="text-gray-500">fingerprint</div><div className="font-mono text-[10px]">{selected.fingerprint ? selected.fingerprint.slice(0, 16) + '…' : '—'}</div>
                <div className="text-gray-500">affected users</div><div>{(selected.affectedUsers || []).length}</div>
              </div>
              {selected.stack && (
                <details className="mt-3">
                  <summary className="text-gray-400 cursor-pointer">Stack trace</summary>
                  <pre className="mt-2 text-[11px] text-gray-300 whitespace-pre-wrap font-mono p-2 rounded-sm" style={{ background: '#0A0A0F', maxHeight: 320, overflow: 'auto' }}>
                    {selected.stack}
                  </pre>
                </details>
              )}
            </div>
          )}
          <div className="mt-4 p-3 rounded-sm" style={{ background: '#13132a', borderLeft: '3px solid #7C3AED' }}>
            <div className="text-xs uppercase" style={{ color: '#7C3AED' }}>AI Triage</div>
            {selected.triage?.classifiedAt ? (
              <div className="text-sm mt-1 space-y-1">
                <div>severity: <strong>{selected.triage.severityRecommendation || '—'}</strong> · effort: <strong>{selected.triage.effortEstimate || '—'}</strong> · confidence: <strong>{selected.triage.confidence ?? '—'}</strong></div>
                <div className="text-gray-300">{selected.triage.proposedAction}</div>
                {selected.triage.proposedSpecOutline && <div className="text-gray-400 text-xs"><em>spec: </em>{selected.triage.proposedSpecOutline}</div>}
                {(selected.triage.likelyFiles || []).length > 0 && (
                  <pre className="text-[11px] text-blue-300 mt-2 whitespace-pre-wrap">{selected.triage.likelyFiles.join('\n')}</pre>
                )}
              </div>
            ) : <div className="text-xs text-gray-500 mt-1">Not yet triaged.</div>}
            <button onClick={retriage} className="mt-2 text-xs px-2 py-1 rounded-sm" style={{ background: '#3B82F6', color: '#fff' }}>Re-run triage</button>
          </div>
          {/* Phase 3: Auto-PR review panel — only renders when an AutoPR exists for this feedback. */}
          <AutoPRPanel feedbackId={selected._id} onChanged={load} />
          <div className="mt-4">
            <div className="text-xs uppercase text-gray-400 mb-1">Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => changeStatus(selected._id, s)} disabled={s === selected.status}
                  className="px-2 py-1 rounded-sm text-xs"
                  style={s === selected.status
                    ? { background: '#7C3AED', color: '#fff' }
                    : { background: '#13132a', color: '#aaa', border: '1px solid #252550' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase text-gray-400 mb-1">Comments</div>
            {(selected.comments || []).map((c, i) => (
              <div key={i} className="text-xs text-gray-300 border-l-2 pl-2 my-1" style={{ borderColor: '#252550' }}>
                <span className="text-gray-500">{c.actor} · {new Date(c.at).toISOString().slice(0, 16).replace('T', ' ')}: </span>{c.body}
              </div>
            ))}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              className="mt-2 w-full px-2 py-1 rounded-sm text-sm"
              style={{ background: '#13132a', color: '#fff', border: '1px solid #252550' }}
            />
            <div className="flex justify-end mt-1">
              <button onClick={addComment} className="text-xs px-2 py-1 rounded-sm" style={{ background: '#3B82F6', color: '#fff' }}>Add comment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
